import { FlexBox } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import type { MainConversation } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import identity from 'lodash/identity';
import { ReactNode, useMemo } from 'react';

import {
  ConversationEventGroup,
  ConversationEventGroupType,
  getConversationEventGroups,
} from 'utils/conversation/groupConversationEvents';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { ConversationAudioEventGroup } from './components/groups/audioEventGroup/conversationAudioEventGroup';
import { ConversationHTMLEventGroup } from './components/groups/htmlEventGroup/conversationHTMLEventGroup';
import { ConversationInstagramStoryEventGroup } from './components/groups/instagramStoryEventGroup/conversationInstagramStoryEventGroup';
import { ConversationInternalNoteEventGroup } from './components/groups/internalNoteEventGroup/conversationInternalNoteEventGroup';
import { ConversationTranscriptGroup } from './components/groups/transcriptEventGroup/conversationTranscriptGroup';
import {
  ConversationEventGroupsAdvancedFilter,
  ConversationEventGroupsGroupConfig,
} from './conversationEventGroups.types';

type ConversationEventGroupsProps = {
  /**
   * The empty state message for when the component isn't able to render any
   * event groups.
   */
  emptyMessage?: ReactNode;
  /** The main conversation. */
  mainConversation: MainConversation;
  /**
   * The groups configuration.
   *
   * Specify the groups and their config props that should be repeated. For
   * further processing, use the advanced filter.
   */
  groupsConfig: readonly ConversationEventGroupsGroupConfig[];
  /** The advanced filter to allow further narrowing. */
  advancedFilter?: ConversationEventGroupsAdvancedFilter;
} & PropsWithClassName;

/** Represents the component for displaying conversation event groups. */
export const ConversationEventGroups = ({
  className,
  emptyMessage,
  groupsConfig,
  mainConversation,
  advancedFilter = identity,
}: ConversationEventGroupsProps): JSX.Element => {
  const { enableCallRecordings } = useFeatureFlag(['enable-call-recordings']);
  const hasRecordingsAccess = useHasAccess('conversation.call_recording');

  const isRecordingAvailable = enableCallRecordings
    ? hasRecordingsAccess
    : false;

  const eventGroups = useMemo(() => {
    const eventGroupTypes = groupsConfig.reduce<
      readonly ConversationEventGroupType[]
    >(
      (groupTypes, groupConfig) => [
        ...groupTypes,
        ...(typeof groupConfig === 'object'
          ? (Object.keys(groupConfig) as readonly ConversationEventGroupType[])
          : [groupConfig]),
      ],
      [],
    );

    return advancedFilter(
      getConversationEventGroups(mainConversation, eventGroupTypes),
    );
  }, [groupsConfig, advancedFilter, mainConversation]);

  const eventGroupComponents = useMemo(() => {
    const { authors } = mainConversation;

    return eventGroups
      .map((eventGroup, index) => {
        switch (eventGroup.type) {
          // TODO: [TEAMS-10805] Implement the status event group components
          case 'group:conversation:agent_assigned':
            return <>conversation agent assigned</>;
          case 'group:conversation:agent_unassigned':
            return <>conversation agent unassigned</>;
          case 'group:conversation:closed':
            return <>conversation closed</>;
          case 'group:conversation:started':
            return <>conversation started</>;
          case 'group:conversation:conversational_assistant':
            return <>conversation conversational assistant</>;
          case 'group:conversation:rating':
            return <>conversation rating</>;
          case 'group:conversation:transfer':
            return <>conversation transfer</>;
          case 'group:conversation:wrapup_message':
            return (
              <ConversationInternalNoteEventGroup
                key={index}
                author={authors[eventGroup.events[0].author_id]}
                events={eventGroup.events}
                {...getConversationEventGroupConfig(groupsConfig, eventGroup)}
              />
            );
          case 'group:instagram_story':
            return (
              <ConversationInstagramStoryEventGroup
                key={index}
                attributes={mainConversation.attributes}
                author={authors[eventGroup.events[0].author_id]}
                events={eventGroup.events}
                {...getConversationEventGroupConfig(groupsConfig, eventGroup)}
              />
            );
          case 'group:email':
          case 'group:messenger':
          case 'group:contact_form':
          case 'group:adachat':
          case 'group:digitalgeniuschat':
          case 'group:kindlychat':
          case 'group:facebook_messenger':
          case 'group:whats_app':
          case 'group:widget_chat':
          case 'group:trustpilotreviews':
          case 'group:sms':
          case 'group:twitter':
            return (
              <ConversationHTMLEventGroup
                key={index}
                author={authors[eventGroup.events[0].author_id]}
                events={eventGroup.events}
                {...getConversationEventGroupConfig(groupsConfig, eventGroup)}
              />
            );
          case 'group:pstn_phone': 
          case 'group:voicemail': {
            const isTranscript = eventGroup.events.some(
              (event) => event.is_transcript === 'true',
            );
            if (isTranscript) {
              return (
                <ConversationTranscriptGroup
                  key={index}
                  author={authors[eventGroup.events[0].author_id]}
                  events={eventGroup.events}
                  {...getConversationEventGroupConfig(groupsConfig, eventGroup)}
                />
              );
            }
            const hasRecordingUrl =
              eventGroup.type === 'group:pstn_phone' &&
              eventGroup.events.some((event) => event.recording_url);
            const hasVoicemailUrl =
              eventGroup.type === 'group:voicemail' &&
              eventGroup.events.some((event) => event.voicemail_url);

            if ((hasRecordingUrl && isRecordingAvailable) || hasVoicemailUrl) {
              return (
                <ConversationAudioEventGroup
                  key={index}
                  author={authors[eventGroup.events[0].author_id]}
                  events={eventGroup.events}
                  {...getConversationEventGroupConfig(groupsConfig, eventGroup)}
                />
              );
            }

            break;
          }
        }
      })
      .filter(Boolean);
  }, [isRecordingAvailable, eventGroups, groupsConfig, mainConversation]);

  return (
    <div className={className}>
      <FlexBox flexDirection="column" gap="300">
        {eventGroupComponents.length > 0 ? eventGroupComponents : emptyMessage}
      </FlexBox>
    </div>
  );
};

/**
 * Gets the configuration of the props for the event group related component.
 *
 * @param groupsConfig The event groups configuration.
 * @param eventGroup The event group to get the configuration for.
 */
function getConversationEventGroupConfig(
  groupsConfig: readonly ConversationEventGroupsGroupConfig[],
  eventGroup: ConversationEventGroup,
):
  | Omit<ConversationEventGroupsGroupConfig, ConversationEventGroupType>
  | undefined {
  const eventGroupConfig = groupsConfig.find(
    (config) => typeof config === 'object' && config[eventGroup.type],
  );
  return eventGroupConfig && eventGroupConfig[eventGroup.type];
}
